import { aiQueries } from "@/features/ai/api/ai-keys";
import { chatQueries } from "@/features/chat";
import { profileQueries } from "@/features/profiles/api/profiles-keys";
import type { WorkspaceMember } from "@/features/workspaces/workspaces.types";
import type {
  Container,
  ContainerMember,
  ContainerMemberInsert,
  CreateProjectInput,
  CreateWorkspaceInput,
  WorkspaceRole,
} from "@shared/types/supabase.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import type { AddMemberInput, Member, MemberActorType, MemberByType } from "../containers.types";
import * as containersApi from "./containers-api";
import { containerQueries } from "./containers-keys";

// CONTAINERS

export function useWorkspaceBySlugQuery(slug: string | undefined) {
  return useQuery({
    ...containerQueries.workspaceBySlug(slug ?? ""),
    enabled: !!slug,
  });
}

export function useProjectBySlugsQuery(
  workspaceSlug: string | undefined,
  projectSlug: string | undefined,
) {
  return useQuery({
    ...containerQueries.projectBySlugs(workspaceSlug ?? "", projectSlug ?? ""),
    enabled: !!workspaceSlug && !!projectSlug,
  });
}

export function useWorkspacesQuery(userId: string | undefined) {
  return useQuery({
    ...containerQueries.workspacesByUser(userId ?? ""),
    enabled: !!userId,
  });
}

/**
 * Membership-only on purpose (CR-203 D2): the combobox, workspace search and
 * MCP list stay scoped to the caller's projects; discovery of every workspace
 * project goes through useWorkspaceProjectsQuery.
 */
export function useProjectsQuery(userId: string, parentContainerId: string | undefined) {
  return useQuery({
    ...containerQueries.projectsByUserAndParentContainerId(userId, parentContainerId ?? ""),
    enabled: !!(userId && parentContainerId),
  });
}

/**
 * Mutation hook for creating projects.
 */
export function useCreateProjectMutation() {
  return useMutation({
    mutationFn: (input: CreateProjectInput) => containersApi.createProject(input),
  });
}

/**
 * Mutation hook for creating workspaces.
 *
 * CR-208: create_workspace consumes the creator's single-use create_workspace permission,
 * so the cached profile (workspace picker, create page) is fetched again after a creation.
 */
export function useCreateWorkspaceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateWorkspaceInput) => containersApi.createWorkspace(input),
    onSuccess: (created) => {
      // created_by is nullable on the workspaces view: fall back to every cached profile.
      void queryClient.invalidateQueries({
        queryKey: created.created_by
          ? profileQueries.byUser(created.created_by).queryKey
          : profileQueries.details(),
      });
    },
  });
}

export function useUpdateContainerMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    Container,
    Error,
    { containerId: string; updates: Partial<Container> },
    {
      previousContainer?: Container;
      containerId: string;
    }
  >({
    mutationFn: ({ containerId, updates }) =>
      containersApi.updateContainer({ containerId, updates }),
    onMutate: async ({ containerId, updates }) => {
      await queryClient.cancelQueries({
        queryKey: containerQueries.byId(containerId).queryKey,
      });

      const previousContainer = queryClient.getQueryData<Container>(
        containerQueries.byId(containerId).queryKey,
      );

      queryClient.setQueryData<Container>(containerQueries.byId(containerId).queryKey, (old) =>
        old
          ? {
              ...old,
              ...updates,
              updated_at: new Date().toISOString(),
            }
          : undefined,
      );

      return {
        previousContainer,
        containerId,
      };
    },
    onError: (error, { containerId }, context) => {
      if (context?.previousContainer) {
        queryClient.setQueryData<Container>(
          containerQueries.byId(containerId).queryKey,
          context.previousContainer,
        );
      }
      console.error("Failed to update container:", error);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<Container>(
        containerQueries.byId(variables.containerId).queryKey,
        data,
      );
    },
    onSettled: (_, __, variables) => {
      void queryClient.invalidateQueries({
        queryKey: containerQueries.byId(variables.containerId).queryKey,
      });

      if (variables.updates.slug && variables.updates.container_type == "workspace") {
        void queryClient.invalidateQueries({
          queryKey: containerQueries.workspaceBySlug(variables.updates.slug).queryKey,
        });
      }

      // TODO: for projects also need to invalidate
    },
  });
}

/**
 * Set or clear an AI chat's default AI persona (CR-46).
 *
 * Patches `containerQueries.byId` optimistically so the members dialog marker,
 * the reply chip and the send-path routing all move together on the click —
 * they all read the room container from that one cache entry. The RPC returns
 * the authoritative row, which then replaces the optimistic one.
 */
export function useSetDefaultAiPersonaMutation() {
  const queryClient = useQueryClient();

  return useMutation<
    Container,
    Error,
    { containerId: string; aiPersonaId: string | null },
    { previousContainer?: Container }
  >({
    // Wrapped rather than passed by reference: react-query calls a mutationFn
    // with (variables, context), and setDefaultAiPersona takes only the former.
    mutationFn: (input) => containersApi.setDefaultAiPersona(input),
    onMutate: async ({ containerId, aiPersonaId }) => {
      const queryKey = containerQueries.byId(containerId).queryKey;
      await queryClient.cancelQueries({ queryKey });

      const previousContainer = queryClient.getQueryData<Container>(queryKey);

      queryClient.setQueryData<Container>(queryKey, (old) =>
        old ? { ...old, default_ai_persona_id: aiPersonaId } : old,
      );

      return { previousContainer };
    },
    onError: (_error, { containerId }, context) => {
      if (context?.previousContainer) {
        queryClient.setQueryData<Container>(
          containerQueries.byId(containerId).queryKey,
          context.previousContainer,
        );
      }
    },
    onSuccess: (data, { containerId }) => {
      queryClient.setQueryData<Container>(containerQueries.byId(containerId).queryKey, data);
    },
  });
}

export function useDeleteContainerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: containersApi.deleteContainer,
    onMutate: async (containerId) => {
      await queryClient.cancelQueries({
        queryKey: containerQueries.byId(containerId).queryKey,
      });

      const previousContainer = queryClient.getQueryData<Container>(
        containerQueries.byId(containerId).queryKey,
      );

      queryClient.removeQueries({
        queryKey: containerQueries.byId(containerId).queryKey,
      });

      return {
        previousContainer,
        containerId,
      };
    },
    onError: (error, containerId, context) => {
      if (context?.previousContainer) {
        queryClient.setQueryData<Container>(
          containerQueries.byId(containerId).queryKey,
          context.previousContainer,
        );
      }
      console.error("Failed to delete container:", error);
    },
    onSuccess: () => {
      console.log("Container deleted successfully");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: containerQueries.all(),
      });
      await queryClient.invalidateQueries({
        queryKey: chatQueries.all(),
      });
    },
  });
}

// CONTAINER MEMBERS

/**
 * Hook to fetch container member row
 */
export function useContainerMemberQuery(userId: string, containerId: string) {
  return useQuery({
    ...containerQueries.membershipByUserAndContainer(userId, containerId),
    enabled: !!userId && !!containerId,
  });
}

/**
 * Hook to fetch container members
 */
export function useContainerMembersQuery<T extends MemberActorType>(
  containerId: string,
  actorTypes: readonly T[],
  search?: string,
): UseQueryResult<MemberByType<T>[], Error> {
  return useQuery({
    queryKey: [...containerQueries.memberships(), containerId, actorTypes, search ?? ""],
    queryFn: () => containersApi.getContainerMembers(containerId, actorTypes, search),
    enabled: !!containerId,
  });
}

/**
 * Mutation hook to create a container member.
 */
export function useUpsertContainerMemberMutation() {
  return useMutation({
    mutationFn: (input: ContainerMemberInsert) => containersApi.upsertContainerMember(input),
  });
}

/**
 * Mutation hook to update a user's container member role. (only human users for now)
 */
export function useUpdateUserContainerMemberRoleMutation(containerId: string) {
  const queryClient = useQueryClient();
  const membersQueryKey = [...containerQueries.memberships(), containerId] as const;
  return useMutation({
    mutationFn: ({
      containerMemberId,
      nextRole,
    }: {
      containerMemberId: string;
      nextRole: WorkspaceRole;
    }) => containersApi.updateUserMemberRole(containerMemberId, nextRole),
    onMutate: async ({ containerMemberId, nextRole }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: membersQueryKey,
      });

      // Snapshot the previous value for rollback
      const previousMembers = queryClient.getQueriesData<Member[]>({
        queryKey: membersQueryKey,
      });

      // Optimistically update cache across all member queries for this container
      queryClient.setQueriesData<Member[]>({ queryKey: membersQueryKey }, (old = []) =>
        Array.isArray(old)
          ? old.map((member) =>
              member.container_member_id === containerMemberId
                ? {
                    ...member,
                    role: nextRole,
                    container_member_updated_at: new Date().toISOString(),
                  }
                : member,
            )
          : old,
      );

      return {
        previousMembers,
        nextRole,
      };
    },
    onError: (error, _variables, context) => {
      if (context?.previousMembers.length) {
        context.previousMembers.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Failed to change membership role:", error);
    },
    onSuccess: (_data, { containerMemberId, nextRole }) => {
      queryClient.setQueriesData({ queryKey: membersQueryKey }, (old) =>
        Array.isArray(old)
          ? old.map((member) =>
              member.container_member_id === containerMemberId
                ? { ...member, role: nextRole }
                : member,
            )
          : old,
      );
      queryClient.setQueryData<ContainerMember>(
        containerQueries.membershipById(containerMemberId).queryKey,
        (old) => (old ? { ...old, role: nextRole } : old),
      );
    },
    onSettled: async () => {
      // Invalidate to ensure consistency with server
      await queryClient.invalidateQueries({
        queryKey: membersQueryKey,
      });
    },
  });
}

export function useRemoveContainerMemberMutation(containerId: string) {
  const queryClient = useQueryClient();
  const membersQueryKey = [...containerQueries.memberships(), containerId];

  return useMutation({
    mutationFn: containersApi.removeMember,
    onMutate: async (containerMemberId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: membersQueryKey,
      });

      // Snapshot the previous value for rollback
      const previousMembers = queryClient.getQueriesData<WorkspaceMember[]>({
        queryKey: membersQueryKey,
      });

      // Optimistically update cache across all member queries for this container
      queryClient.setQueriesData<WorkspaceMember[]>(
        { queryKey: membersQueryKey },
        (current = []) =>
          Array.isArray(current)
            ? current.filter((member) => member.id !== containerMemberId)
            : current,
      );

      return { previousMembers };
    },
    onError: (error, _variables, context) => {
      if (context?.previousMembers.length) {
        context.previousMembers.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error("Failed to remove member:", error);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: membersQueryKey });

      await queryClient.invalidateQueries({
        queryKey: aiQueries.list().queryKey,
      });

      await queryClient.invalidateQueries({
        queryKey: containerQueries.all(),
      });

      await queryClient.invalidateQueries({
        queryKey: ["chats"],
      });
    },
  });
}

export function useAddMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddMemberInput) => containersApi.addMember(input),
    onSettled: async (_created, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: containerQueries.membersByContainer(variables.p_container_id, ["user"]).queryKey,
      });

      await queryClient.invalidateQueries({
        queryKey: containerQueries.membersByContainer(variables.p_container_id, ["invited_user"])
          .queryKey,
      });

      await queryClient.invalidateQueries({
        queryKey: containerQueries.membersByContainer(variables.p_container_id, ["ai"]).queryKey,
      });

      await queryClient.invalidateQueries({
        queryKey: aiQueries.list().queryKey,
      });

      // CR-67: a message room's Slack-style title is derived from its members,
      // so adding a member must refresh the batch member-names query behind the
      // sidebar/list titles (the header reads membersByContainer, already
      // invalidated above). Removal already invalidates chatQueries.all().
      await queryClient.invalidateQueries({
        queryKey: chatQueries.messageRoomLists(),
      });
    },
  });
}
